import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { TrendingUp, TrendingDown, Wallet, Clock } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import DashboardArang from "@/pages/DashboardArang";
import PajakReminderList from "@/components/app/PajakReminderList";
import ChartRangeFilter, { rangeLabel } from "@/components/app/ChartRangeFilter";

const Stat = ({ label, value, icon: Icon, tone, testid }) => (
  <div data-testid={testid} className="card-flat p-4 md:p-5">
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`h-9 w-9 flex items-center justify-center rounded-sm ${tone}`}>
        <Icon size={16} className="text-white"/>
      </div>
    </div>
    <div className="mt-3 md:mt-4 font-mono text-lg md:text-2xl tabular-nums text-slate-900">{rupiah(value)}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  const [range, setRange] = useState({ key: "default", start: "", end: "" });

  const isArang = user?.workspace_type === "arang";

  useEffect(() => {
    if (isArang) return; // Arang dashboard handled by DashboardArang
    api.get("/dashboard").then(r => setD(r.data));
  }, [isArang]);
  useEffect(() => {
    if (isArang) return;
    const params = range.start ? { start: range.start, end: range.end } : {};
    api.get("/dashboard/trend", { params }).then(r => setTrend(r.data)).catch(() => setTrend([]));
  }, [isArang, range]);

  if (isArang) return <DashboardArang/>;

  const chartData = trend.map(t => ({
    name: t.label || t.bulan.split(" ")[0].slice(0,3),
    full: t.bulan,
    pemasukan: t.pemasukan,
    pengeluaran: t.pengeluaran,
    laba: t.pemasukan - t.pengeluaran,
  }));

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Ringkasan keuangan Anda saat ini."/>
      <div className="p-4 md:p-8 space-y-6">
        <PajakReminderList/>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          <Stat testid="stat-pemasukan" label="Total Pendapatan" value={d?.total_pemasukan||0} icon={TrendingUp} tone="bg-emerald-600"/>
          <Stat testid="stat-pengeluaran" label="Total Pengeluaran" value={d?.total_pengeluaran||0} icon={TrendingDown} tone="bg-rose-600"/>
          <Stat testid="stat-laba" label="Laba Bersih" value={d?.laba_bersih||0} icon={Wallet} tone="bg-[color:var(--brand)]"/>
          <Stat testid="stat-piutang" label="Piutang Berjalan" value={d?.total_piutang||0} icon={Clock} tone="bg-amber-600"/>
        </div>

        <div className="card-flat p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="text-base md:text-lg font-semibold text-slate-900" data-testid="chart-title">Tren — {rangeLabel(range, "6 Bulan Terakhir")}</h3>
              <p className="text-xs md:text-sm text-slate-500">Perbandingan pendapatan, pengeluaran, dan laba bersih{trend[0]?.granularity === "day" ? " per hari" : " per bulan"}.</p>
            </div>
            <ChartRangeFilter value={range} onChange={setRange} defaultLabel="6 Bulan Terakhir" testid="dash-chart-filter"/>
          </div>
          <div style={{width: "100%", height: 300}}>
            <ResponsiveContainer width="100%" height={300} initialDimension={{ width: 800, height: 300 }}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="name" stroke="#64748B" style={{fontSize: 12}} minTickGap={8}/>
                <YAxis stroke="#64748B" style={{fontSize: 11}} tickFormatter={(v)=>Math.abs(v) >= 1_000_000 ? `${(v/1_000_000).toFixed(Math.abs(v) >= 10_000_000 ? 0 : 1)}jt` : Math.abs(v) >= 1000 ? `${(v/1000).toFixed(0)}rb` : `${v}`}/>
                <Tooltip formatter={(v)=>rupiah(v)} labelFormatter={(_l, p)=>p?.[0]?.payload?.full || _l} contentStyle={{fontSize: 12, borderRadius: 4, borderColor: "#166db4"}}/>
                <Legend wrapperStyle={{fontSize: 12}}/>
                <Bar dataKey="pemasukan" name="Pemasukan" fill="#166db4" radius={[3,3,0,0]}/>
                <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#EF4444" radius={[3,3,0,0]}/>
                <Line type="monotone" dataKey="laba" name="Laba Bersih" stroke="#10B981" strokeWidth={2} dot={{r:4}}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
